import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  CONTENT_CARD_LAYOUT_TEMPLATES,
  normalizeContentCardsLayout
} from './contentCardLayoutTemplates';
import type { ContentCardsLayoutTemplate } from './types';

interface ContentCardsLayoutPreviewProps {
  value: ContentCardsLayoutTemplate;
  onChange: (layout: ContentCardsLayoutTemplate) => void;
  /** Verilirse yalnızca bu yerleşimler listelenir (promo sade mod) */
  allowedLayouts?: ContentCardsLayoutTemplate[];
}

const LayoutMock: React.FC<{
  layout: ContentCardsLayoutTemplate;
  selected: boolean;
  label: string;
  description: string;
  onClick: () => void;
}> = ({ layout, selected, label, description, onClick }) => (
  <Box
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') onClick();
    }}
    sx={{
      cursor: 'pointer',
      borderRadius: 2,
      border: '2px solid',
      borderColor: selected ? 'primary.main' : 'divider',
      bgcolor: selected ? 'rgba(25, 118, 210, 0.06)' : '#fff',
      p: 1.25,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      transition: 'all 0.2s ease',
      position: 'relative',
      '&:hover': { borderColor: 'primary.light', boxShadow: 2, transform: 'translateY(-1px)' }
    }}
  >
    {selected && (
      <Chip
        icon={<CheckCircleIcon />}
        label="Seçili"
        size="small"
        color="primary"
        sx={{ position: 'absolute', top: 6, right: 6, height: 20, fontSize: '0.65rem' }}
      />
    )}
    <Box
      sx={{
        height: 72,
        borderRadius: 1.25,
        bgcolor: '#f1f5f9',
        border: '1px solid #e2e8f0',
        p: 0.75,
        mb: 1,
        display: 'flex',
        gap: 0.6
      }}
    >
      {layout === 'grid' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5, width: '100%' }}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Box key={i} sx={{ bgcolor: '#94a3b8', borderRadius: 0.5, minHeight: 18 }} />
          ))}
        </Box>
      )}
      {layout === 'sidebar-left' && (
        <>
          <Box sx={{ width: '34%', bgcolor: '#3b82f6', borderRadius: 0.5, minHeight: '100%' }} />
          <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
            {[0, 1, 2, 3].map((i) => (
              <Box key={i} sx={{ bgcolor: '#94a3b8', borderRadius: 0.5, minHeight: 18 }} />
            ))}
          </Box>
        </>
      )}
      {layout === 'sidebar-right' && (
        <>
          <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
            {[0, 1, 2, 3].map((i) => (
              <Box key={i} sx={{ bgcolor: '#94a3b8', borderRadius: 0.5, minHeight: 18 }} />
            ))}
          </Box>
          <Box sx={{ width: '34%', bgcolor: '#3b82f6', borderRadius: 0.5, minHeight: '100%' }} />
        </>
      )}
      {layout === 'featured' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
          <Box sx={{ bgcolor: '#3b82f6', borderRadius: 0.5, height: 22 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5, flex: 1 }}>
            {[0, 1, 2].map((i) => (
              <Box key={i} sx={{ bgcolor: '#94a3b8', borderRadius: 0.5, minHeight: 18 }} />
            ))}
          </Box>
        </Box>
      )}
      {layout === 'custom' && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 0.5,
            width: '100%',
            alignContent: 'start'
          }}
        >
          <Box sx={{ gridColumn: 'span 2', bgcolor: '#3b82f6', borderRadius: 0.5, minHeight: 22 }} />
          <Box sx={{ gridColumn: 'span 1', bgcolor: '#94a3b8', borderRadius: 0.5, minHeight: 22 }} />
          <Box sx={{ gridColumn: 'span 1', bgcolor: '#94a3b8', borderRadius: 0.5, minHeight: 22 }} />
          <Box sx={{ gridColumn: 'span 1', bgcolor: '#94a3b8', borderRadius: 0.5, minHeight: 22 }} />
          <Box sx={{ gridColumn: 'span 3', bgcolor: '#64748b', borderRadius: 0.5, minHeight: 22 }} />
        </Box>
      )}
      {layout === 'promo-trio' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5, width: '100%' }}>
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                bgcolor: i === 0 ? '#1e293b' : '#64748b',
                borderRadius: 0.5,
                minHeight: '100%',
                position: 'relative',
                display: 'flex',
                alignItems: 'flex-end',
                p: 0.4
              }}
            >
              <Box sx={{ width: '70%', height: 4, bgcolor: 'rgba(255,255,255,0.85)', borderRadius: 0.5 }} />
            </Box>
          ))}
        </Box>
      )}
      {(layout === 'promo-large-left' || layout === 'promo-large-right') && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 0.5,
            width: '100%',
            direction: layout === 'promo-large-right' ? 'rtl' : 'ltr'
          }}
        >
          <Box
            sx={{
              bgcolor: '#1e293b',
              borderRadius: 0.5,
              minHeight: '100%',
              display: 'flex',
              alignItems: 'flex-end',
              p: 0.4,
              direction: 'ltr'
            }}
          >
            <Box sx={{ width: '60%', height: 4, bgcolor: 'rgba(255,255,255,0.85)', borderRadius: 0.5 }} />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateRows: '1fr 1fr', gap: 0.5, direction: 'ltr' }}>
            {[0, 1].map((i) => (
              <Box
                key={i}
                sx={{
                  bgcolor: '#64748b',
                  borderRadius: 0.5,
                  display: 'flex',
                  alignItems: 'flex-end',
                  p: 0.35
                }}
              >
                <Box sx={{ width: '55%', height: 3, bgcolor: 'rgba(255,255,255,0.8)', borderRadius: 0.5 }} />
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.25, pr: 4 }}>
      {label}
    </Typography>
    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.35, flex: 1 }}>
      {description}
    </Typography>
  </Box>
);

const ContentCardsLayoutPreview: React.FC<ContentCardsLayoutPreviewProps> = ({
  value,
  onChange,
  allowedLayouts
}) => {
  const active = normalizeContentCardsLayout(value);
  const templates = allowedLayouts?.length
    ? CONTENT_CARD_LAYOUT_TEMPLATES.filter((t) => allowedLayouts.includes(t.id))
    : CONTENT_CARD_LAYOUT_TEMPLATES;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: templates.length <= 3 ? `repeat(${templates.length}, 1fr)` : 'repeat(3, 1fr)',
          lg: templates.length <= 3 ? `repeat(${templates.length}, 1fr)` : 'repeat(4, 1fr)'
        },
        gap: 1.25
      }}
    >
      {templates.map((template) => (
        <LayoutMock
          key={template.id}
          layout={template.id}
          selected={active === template.id}
          label={template.label}
          description={template.description}
          onClick={() => onChange(template.id)}
        />
      ))}
    </Box>
  );
};

export default ContentCardsLayoutPreview;
